/**
 * TCL read-more — collapses over-long prose behind a "Read more" toggle.
 *
 * Site-wide, measured, and unconditional on content: nothing in Liquid decides
 * what is long. Length depends on the viewport, the font that actually loaded and
 * the column the text ended up in, none of which a template can know. So the
 * browser measures, and a block is clamped only if it really is taller than
 * `lines` lines where the visitor is standing.
 *
 * ── IT DOES NOT WRAP ANYTHING ───────────────────────────────────────────────
 * The clamp is applied to the prose element ITSELF and the button is inserted as
 * its next sibling. Moving the text into a new wrapper would re-parent every text
 * node in it, and this theme has features that walk text nodes directly —
 * `scroll-highlight` reveals words via a TreeWalker, the TOC harvests headings.
 * A DOM-preserving clamp cannot break them.
 *
 * ── THE FADE IS A MASK, NOT AN OVERLAY ──────────────────────────────────────
 * The usual recipe is an absolutely-positioned gradient pseudo-element coloured to
 * match the background. That needs `position: relative` on the prose element (which
 * silently re-parents any absolutely-positioned descendant it has) AND it needs to
 * know the background colour — and these sections run on six different colour
 * schemes, so it would be a grey smear on three of them. `mask-image` fades the
 * TEXT instead: no positioning, no stacking context, correct on every scheme, and
 * it is one property to remove when expanded.
 *
 * ── WHY IT RUNS ON `document.fonts.ready` ───────────────────────────────────
 * The theme preloads two webfonts. Measuring before they swap means measuring the
 * fallback's metrics, which mis-measures the line count in both directions — so
 * the first pass waits for the fonts, and DOMContentLoaded is only the fallback for
 * a browser with no Font Loading API.
 *
 * Opt out with `data-tcl-readmore="off"` on the element or any ancestor.
 */

(function () {
  'use strict';

  var cfg = window.tclReadMore || {};
  var LINES = parseInt(cfg.lines, 10) > 0 ? parseInt(cfg.lines, 10) : 5;
  var MORE = cfg.more || 'Read more';
  var LESS = cfg.less || 'Read less';

  /* A lone paragraph is only clamped if it is genuinely long-form. Without this,
     a perfectly ordinary 200-character paragraph exceeds five lines on a 390px
     phone and every page turns into a wall of "Read more" buttons — the measure
     would be right and the result still wrong. Container-tier blocks have no such
     floor: a `.rte` that is five lines tall is five lines of prose either way. */
  var MIN_PARAGRAPH_CHARS = 320;

  /* Known prose REGIONS, clamped as a whole so several short paragraphs that add
     up to a wall of text collapse together. `[class*="__body"]` is the theme's own
     convention for a section's prose slot (tl-treatment-info__body,
     tl-ti-csec__body, tl-proc__body, …), so new sections are covered without
     touching this list. */
  var CONTAINERS = '.rte, [class*="__body"], [data-tcl-readmore=""], [data-tcl-readmore="on"]';

  /* Structural exclusions, checked with `closest` rather than written into the
     selector, because what disqualifies a block is where it SITS:
       - `details`  — the accordion animates its own height via ::details-content
                      (PR #126). A max-height clamp inside it fights that.
       - `.tl-tcard`, `.tl-navdrop` — cards and nav are teasers already, and the
                      nav renders on every page.
       - `form`, `nav`, `figcaption`, `blockquote` — not body prose.
       - `.tl-rm`   — never nest a clamp inside a clamp. */
  /*   - `.reviews-wall` — measured: it clamped 6 review cards on a treatment page,
         3 of which were `cloneNode(true)` COPIES the marquee tiles for its loop.
         cloneNode does not copy event listeners, so every button on a clone was
         dead on arrival — and `assets/reviews-wall.js` already implements its own
         read-more for those cards and deliberately drops the toggle on the tiled
         ones for exactly this reason. Two mechanisms on one element is worse than
         either.
       - `[aria-hidden="true"]` — the general form of that: a subtree hidden from
         assistive tech is a decorative duplicate or a clone, and a control inside
         one is unreachable by definition. */
  /*   - `[class*="hero"]` — A HERO IS NOT BODY PROSE. It is the page's opening
         statement and the first thing above the fold, so a "Read more" there
         collapses the one paragraph a visitor came to read and puts a control
         where the page should be making its case. Measured before this exclusion:
         `.tl-treatment-hero__tagline` (661 characters, 8 lines) collapsed on all
         32 treatment pages. Matched as a SUBSTRING of the class so it covers
         `tl-treatment-hero__tagline`, `tl-treatment-hero__copy` and `tl-hero__copy`
         at once, and covers a hero this theme has not grown yet — the whole
         point of excluding a REGION rather than three class names. `closest`
         includes the element itself, so a hero paragraph is caught whether the
         class is on it or on an ancestor. */
  var BLOCKED_ANCESTORS =
    'details, form, nav, figcaption, blockquote, .tl-tcard, .tl-navdrop, .tl-rm, ' +
    '.reviews-wall, [class*="hero"], [aria-hidden="true"], [data-tcl-readmore="off"]';

  /* Content that makes a height clamp the wrong tool. A block containing a 300px
     image has a height that has nothing to do with its line count, so a five-line
     clamp hides almost all of it behind a fade — the fade then reads as a bug
     rather than an affordance. Same for anything interactive: clipping a control
     out of view leaves it focusable but invisible. */
  /*   - `h1`-`h6` — a block with a heading in it is a STRUCTURED region, not a
         paragraph, and a five-line clamp on one hides everything under the
         heading: measured on the homepage flagship band, where clamping
         `.tl-flagship__body` left the heading visible and collapsed all the prose
         below it to nothing. Its paragraphs are still eligible individually, which
         is the right granularity for structured content. */
  var BLOCKED_CONTENT =
    'img, picture, video, iframe, svg, table, input, textarea, select, button, form, h1, h2, h3, h4, h5, h6';

  /* THE TOGGLE IS A SIBLING, SO THE PARENT'S LAYOUT MODE DECIDES WHERE IT LANDS.
     In a grid parent it takes its own CELL, and in a flex row its own item — on the
     flagship band it appeared under the PHOTO, a column away from the text it
     controls, which is worse than no toggle at all.

     Fixed by refusing the element rather than by patching the button's placement:
     copying an element's grid/flex placement onto an inserted sibling means
     re-deriving `grid-area`, `order`, `align-self` and the implicit-track rules
     from computed styles, and getting any of it wrong puts the control somewhere
     arbitrary. A parent in normal block flow puts the button immediately under its
     text with no guessing, and every structured layout has prose INSIDE it that
     the paragraph tier reaches anyway. */
  var FLOW_DISPLAYS = ['block', 'flow-root', 'list-item', 'table-cell'];

  /* A SINGLE-LINE FLEX COLUMN IS ALSO SAFE. tcl-* layout containers stack their
     children with `display: flex; flex-direction: column` + gap, and in a column
     that does not wrap, an inserted sibling lands directly under the element it
     follows — the same placement block flow gives. Rows, wrapping columns, grids
     and an element carrying its own `order` stay refused, for the reason above. */
  function isStack(cs) {
    return (
      (cs.display === 'flex' || cs.display === 'inline-flex') &&
      cs.flexDirection === 'column' &&
      cs.flexWrap === 'nowrap'
    );
  }

  function insertionSafe(el) {
    var parent = el.parentElement;
    if (!parent) return false;
    var cs = window.getComputedStyle(parent);
    if (FLOW_DISPLAYS.indexOf(cs.display) !== -1) return true;
    return isStack(cs) && window.getComputedStyle(el).order === '0';
  }

  var els = [];
  var resizeTimer = null;

  /* WITH MOTION REDUCED THERE IS NO `transitionend`, so the pixel height that
     `expand` sets to animate the growth would never be released and the block
     would be frozen at today's height — unable to reflow on a resize or a font
     swap. Checked here rather than left to the CSS, because the CSS's
     `transition: none` is exactly what removes the event this code waits for. */
  var REDUCE_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function lineLimit(el) {
    var cs = window.getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight);
    if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.5;
    return lh * LINES;
  }

  function eligible(el) {
    if (el.dataset.tclRmBound === 'true') return false;
    if (el.closest(BLOCKED_ANCESTORS)) return false;
    if (el.querySelector(BLOCKED_CONTENT)) return false;
    if (!insertionSafe(el)) return false;
    /* A block whose own text is empty (a spacer, a wrapper around a widget) has
       nothing to collapse. */
    if (!el.textContent || el.textContent.trim().length < 40) return false;
    return true;
  }

  function collapse(el) {
    el.classList.add('tl-rm', 'is-collapsed');
    el.style.maxHeight = el.__tclRmLimit + 'px';
  }

  function expand(el) {
    el.classList.remove('is-collapsed');
    if (REDUCE_MOTION) {
      el.style.maxHeight = 'none';
      return;
    }
    /* An explicit pixel height so the growth animates; released to `none` once the
       transition lands, or the block would be frozen at today's height and could
       not reflow on a resize or a font swap. */
    el.style.maxHeight = el.scrollHeight + 'px';
  }

  function release(el) {
    if (!el.classList.contains('is-collapsed')) el.style.maxHeight = 'none';
  }

  function unclamp(el) {
    el.classList.remove('tl-rm', 'is-collapsed');
    el.style.maxHeight = '';
    if (el.__tclRmToggle) el.__tclRmToggle.hidden = true;
  }

  function bind(el, index) {
    /* `scan` can legitimately find the same element twice — a `<p>` whose class
       ends in `__body` matches both tiers — and `eligible` runs before either push,
       so neither call has seen the other. Without this the element gets two
       buttons, and the second `collapse` writes a clamp the first button's state no
       longer describes. */
    if (el.dataset.tclRmBound === 'true') return;

    /* RE-CHECKED HERE, AT BIND TIME, and not only in `eligible`. `eligible` runs
       during collection, when no clamp has been applied yet, so it cannot see a
       clamp that a LATER element in the same batch is about to get. Two nested
       containers — a `.rte` inside a `[class*="__body"]` — would both pass
       collection and both clamp. `closest` is asked again once the outer one is
       already marked, which is the only moment the answer is true. */
    if (el.parentElement && el.parentElement.closest('.tl-rm')) return;

    var limit = lineLimit(el);
    /* THE TEST IS "IS THERE ENOUGH HIDDEN TO BE WORTH REVEALING", not "is it over
       the limit". A half-line tolerance was not enough: measured on a treatment
       page, a `tl-treatment-info__body` of 129px against a 115.5px limit bound and
       its "Read more" revealed 13 pixels — one clipped line. A toggle that opens
       onto almost nothing reads as a bug, and a six-line block is the COMMON case
       on a wide screen, not an edge one. So at least a line and a half has to be
       hidden before the block is worth collapsing at all. */
    if (el.scrollHeight <= limit + (limit / LINES) * 1.5) return;

    el.dataset.tclRmBound = 'true';
    el.__tclRmLimit = limit;

    if (!el.id) el.id = 'tl-rm-' + index + '-' + Math.random().toString(36).slice(2, 8);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tl-rm__toggle';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', el.id);
    btn.textContent = MORE;

    btn.addEventListener('click', function () {
      var isCollapsed = el.classList.contains('is-collapsed');
      if (isCollapsed) {
        expand(el);
        btn.setAttribute('aria-expanded', 'true');
        btn.textContent = LESS;
      } else {
        /* Pin the current height first, then drop to the clamp on the next frame —
           a transition from `none` to a pixel value does not animate. */
        el.style.maxHeight = el.scrollHeight + 'px';
        requestAnimationFrame(function () {
          collapse(el);
        });
        btn.setAttribute('aria-expanded', 'false');
        btn.textContent = MORE;
      }
    });

    el.addEventListener('transitionend', function (event) {
      if (event.propertyName === 'max-height') release(el);
    });

    el.__tclRmToggle = btn;
    el.insertAdjacentElement('afterend', btn);
    /* In a stack the parent's gap already separates the toggle from its text, and
       the toggle's own top margin would ADD to it (flex items do not collapse
       margins) — a 2.4rem gap would float "Read more" away from the prose it
       controls. Pull it back so it sits the same 0.4rem under the text as in
       block flow. */
    var pcs = window.getComputedStyle(el.parentElement);
    if (isStack(pcs)) {
      var gap = parseFloat(pcs.rowGap) || 0;
      if (gap) btn.style.marginTop = 'calc(var(--tl-space-3xs) - ' + gap + 'px)';
    }
    collapse(el);
    els.push(el);
  }

  /* THE TWO TIERS BIND SEPARATELY, AND THAT ORDER IS THE WHOLE CORRECTNESS
     ARGUMENT — not tidiness.

     `eligible` disqualifies anything inside an existing `.tl-rm`, which is what
     stops a clamp nesting inside a clamp. That test can only see clamps that have
     ALREADY been applied. Collecting both tiers and binding afterwards therefore
     tested every paragraph against a page where no container was clamped yet, and
     a paragraph inside an eligible container passed.

     Measured, not theorised: on the homepage the flagship band's
     `.tl-flagship__body` (a container, 985px tall) and the `.tl-flagship__desc`
     paragraph INSIDE it were both clamped — two buttons, the inner one clipped
     invisible by the outer clamp, and the inner text hidden twice over.

     So tier 1 binds completely before tier 2 is even enumerated. */
  function scan() {
    var main = document.getElementById('MainContent') || document.body;

    var containers = [];
    Array.prototype.forEach.call(main.querySelectorAll(CONTAINERS), function (el) {
      if (eligible(el)) containers.push(el);
    });
    /* Outermost first, so a container nested in another container is disqualified
       by the outer one's clamp rather than by document order. `querySelectorAll`
       is already in tree order, which is outermost-first for an ancestor/descendant
       pair — this loop only has to not re-sort it. */
    containers.forEach(bind);

    var paragraphs = [];
    Array.prototype.forEach.call(main.querySelectorAll('p'), function (el) {
      if (el.textContent.trim().length < MIN_PARAGRAPH_CHARS) return;
      if (eligible(el)) paragraphs.push(el);
    });
    paragraphs.forEach(bind);
  }

  /* Re-measure on resize: the line limit is a function of the computed
     line-height, and a block that needed clamping in portrait may not in
     landscape. Only ever relaxes or re-tightens an already-bound block — it does
     not go looking for new ones, so a resize cannot make buttons appear under text
     the visitor was reading. */
  function remeasure() {
    els.forEach(function (el) {
      el.__tclRmLimit = lineLimit(el);
      if (el.classList.contains('is-collapsed')) {
        el.style.maxHeight = el.__tclRmLimit + 'px';
        if (el.scrollHeight <= el.__tclRmLimit) unclamp(el);
      } else if (el.__tclRmToggle) {
        el.__tclRmToggle.hidden = false;
      }
    });
  }

  function start() {
    scan();
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(remeasure, 200);
    });
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function () { scan(); });
    }
  }

  function ready() {
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(start);
    else start();
  }

  if (document.readyState !== 'loading') ready();
  else document.addEventListener('DOMContentLoaded', ready);
})();
